/** A circuit, large enough to read, with everything the survey knows on it.
 *
 * `TrackOutline` is the thumbnail: two borders and a finish line at card
 * size, and nothing to do but look. This is the same geometry as a map —
 * the road surface, the gaps nobody has driven, the walls, the centre line —
 * each a layer that can be switched off, in a view that zooms to a corner
 * and slides along a straight.
 *
 * It takes a compiled document and nothing else. The circuit's page gives it
 * the published one; a screen that holds some other document of the same
 * shape — what a waiting pull request would publish, say — can hand that
 * over instead and get the same map, with `children` for whatever it wants
 * drawn on top, in the drawing space `TrackOutline` defines.
 *
 * Wheel, drag, pinch-free: the wheel zooms about the cursor, a drag pans,
 * and the three buttons and the arrow keys do the same for anybody without
 * a wheel or a mouse. The arithmetic is `mapView.ts`, which is where it is
 * tested; this file is the part that needs a browser.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
} from 'react';

import {
  fitViewBox,
  geometryBounds,
  hasGeometry,
  pathFromPoints,
  runToPoints,
  viewBoxString,
} from '@/components/TrackOutline';
import type { ViewBox } from '@/components/TrackOutline';
import type { TrackGeometry } from '@/lib/types';
import {
  DEFAULT_LAYERS,
  MAP_LAYERS,
  NAME_ZOOM,
  centrelinePath,
  cornerLabel,
  fillAspect,
  focusOn,
  groundToPoint,
  labelPoint,
  layerCounts,
  panBy,
  roadPath,
  scaleBar,
  scaleLabel,
  segmentsPath,
  viewCentre,
  zoomAt,
  zoomOf,
} from '@/components/tracks/mapView';
import type { LayerState, MapLayer } from '@/components/tracks/mapView';

const LAYER_COLOUR: Record<MapLayer, string> = {
  road: 'color-mix(in srgb, var(--color-text) 22%, transparent)',
  borders: 'var(--color-neutral-300)',
  gaps: 'var(--color-warn)',
  walls: 'var(--color-error)',
  centerline: 'var(--color-accent)',
  finish: 'var(--color-text)',
  corners: 'var(--color-accent)',
  sections: 'var(--color-neutral-400)',
};

/** One notch of the wheel, one press of a button. */
const WHEEL_STEP = 1.2;
const BUTTON_STEP = 1.6;
/** One press of an arrow key, as a fraction of the view. */
const KEY_PAN = 0.15;
/** How far in the map goes when a corner is picked from a list: about 400 m
 *  across on a 5 km circuit, which is the corner and the road either side. */
const FOCUS_ZOOM = 6;

/** Somewhere for the map to go and look. `key` makes picking the same corner
 *  twice a new request, so the map returns to it after being dragged away. */
export interface MapFocus {
  x: number;
  z: number;
  key: string | number;
  /** How many metres across the view should show. A corner wants the road
   *  either side of it; a twelve-metre gap wants a great deal less. Without
   *  one the map goes in by `FOCUS_ZOOM`. */
  span?: number;
}

export interface TrackMapProps {
  geometry: TrackGeometry;
  /** Names the drawing for assistive technology: "Map of Tsukuba Circuit". */
  title: string;
  height?: number | string;
  /** Layers shown to begin with. */
  initialLayers?: LayerState;
  /** Centres the map on a ground position, magnified. */
  focus?: MapFocus | null;
  /** The corner to draw picked out, by its number. */
  selectedCorner?: number | null;
  /** Drawn over the layers, in the same space: world x, world z negated. */
  children?: ReactNode;
  style?: CSSProperties;
}

interface Size {
  width: number;
  height: number;
}

export function TrackMap({
  geometry,
  title,
  height = 460,
  initialLayers = DEFAULT_LAYERS,
  focus = null,
  selectedCorner = null,
  children,
  style,
}: TrackMapProps) {
  const frame = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });
  const [layers, setLayers] = useState<LayerState>(initialLayers);
  /** Null is "fitted": the view follows the element until somebody moves it. */
  const [moved, setMoved] = useState<ViewBox | null>(null);
  const drag = useRef<{ id: number; x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const element = frame.current;
    if (!element) return undefined;
    const read = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(read);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const base = useMemo<ViewBox | null>(() => {
    const box = hasGeometry(geometry) ? geometryBounds(geometry) : null;
    return box ? fillAspect(fitViewBox(box, 0.05), size.width, size.height) : null;
  }, [geometry, size.width, size.height]);

  // A different circuit, or a different shape of card, starts fitted again —
  // unless the page has asked to look at something, which outlives both.
  useEffect(() => {
    const zoom = base && focus?.span && focus.span > 0 ? base.width / focus.span : FOCUS_ZOOM;
    setMoved(base && focus ? focusOn(base, groundToPoint(focus), zoom) : null);
    // `focus.key` is the request, and the position comes with it: a parent
    // that rebuilds the same focus object on every render has not asked again.
  }, [base, focus?.key]);

  const paths = useMemo(
    () => ({
      road: roadPath(geometry.road),
      borders: [...(geometry.borders?.L ?? []), ...(geometry.borders?.R ?? [])]
        .map((run) => pathFromPoints(runToPoints(run)))
        .filter(Boolean)
        .join(' '),
      gaps: segmentsPath([...(geometry.gaps?.L ?? []), ...(geometry.gaps?.R ?? [])]),
      walls: segmentsPath(geometry.walls),
      centerline: centrelinePath(geometry.centerline),
      finish: segmentsPath(geometry.finish ? [geometry.finish] : []),
    }),
    [geometry],
  );
  const counts = useMemo(() => layerCounts(geometry), [geometry]);

  const view = moved ?? base;

  const change = useCallback(
    (next: (current: ViewBox, fitted: ViewBox) => ViewBox) => {
      if (!base) return;
      setMoved((current) => {
        const result = next(current ?? base, base);
        return zoomOf(result, base) <= 1 ? null : result;
      });
    },
    [base],
  );

  const fittedNow = useRef(true);
  fittedNow.current = moved === null;

  // React registers `wheel` as passive, and a passive listener cannot stop
  // the page scrolling underneath the map it is zooming. A fitted map has
  // nowhere further out to go, so scrolling down over one scrolls the page:
  // a map that swallowed that would be a trap half way down the screen.
  useEffect(() => {
    const element = frame.current;
    if (!element) return undefined;
    const onWheel = (event: WheelEvent) => {
      if (event.deltaY >= 0 && fittedNow.current) return;
      event.preventDefault();
      const bounds = element.getBoundingClientRect();
      if (!(bounds.width > 0 && bounds.height > 0)) return;
      const factor = event.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
      change((current, fitted) =>
        zoomAt(
          current,
          fitted,
          factor,
          (event.clientX - bounds.left) / bounds.width,
          (event.clientY - bounds.top) / bounds.height,
        ),
      );
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [change]);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !moved) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
    setDragging(true);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const held = drag.current;
    if (!held || held.id !== event.pointerId || !(size.width > 0 && size.height > 0)) return;
    // A release can be lost — let go over another window, swallowed by a
    // context menu — and a map that went on following a pointer nobody is
    // holding would be unusable until the page was reloaded.
    if ((event.buttons & 1) === 0) {
      drag.current = null;
      setDragging(false);
      return;
    }
    const dx = (held.x - event.clientX) / size.width;
    const dy = (held.y - event.clientY) / size.height;
    drag.current = { ...held, x: event.clientX, y: event.clientY };
    change((current, fitted) => panBy(current, fitted, dx, dy));
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current?.id !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, (current: ViewBox, fitted: ViewBox) => ViewBox> = {
      '+': (current, fitted) => zoomAt(current, fitted, BUTTON_STEP),
      '=': (current, fitted) => zoomAt(current, fitted, BUTTON_STEP),
      '-': (current, fitted) => zoomAt(current, fitted, 1 / BUTTON_STEP),
      '0': (_current, fitted) => fitted,
      ArrowLeft: (current, fitted) => panBy(current, fitted, -KEY_PAN, 0),
      ArrowRight: (current, fitted) => panBy(current, fitted, KEY_PAN, 0),
      ArrowUp: (current, fitted) => panBy(current, fitted, 0, -KEY_PAN),
      ArrowDown: (current, fitted) => panBy(current, fitted, 0, KEY_PAN),
    };
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    change(move);
  };

  const zoom = view && base ? zoomOf(view, base) : 1;
  const bar = view ? scaleBar(view, size.width, size.height) : null;
  // Text in a viewBox is sized in the viewBox's units, so it is sized from
  // the view and stays the same height on screen at every zoom.
  const labelSize = view ? view.height * 0.028 : 0;
  // Labels are pushed away from the middle of the circuit, not of the view:
  // a corner's number must not swap sides of the road as the map pans.
  const centre = base ? viewCentre(base) : null;
  const named = zoom >= NAME_ZOOM;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, ...style }}>
      <div
        ref={frame}
        role="group"
        aria-label={`${title}. Scroll or use plus and minus to zoom, drag or use the arrow keys to move.`}
        tabIndex={0}
        // A press that starts on the map must not start a text selection that
        // the drag then sweeps across the rest of the page. Cancelling the
        // press also cancels the focus it would have given, so that is put
        // back by hand: the arrow keys work on the map that was clicked.
        onMouseDown={(event) => {
          event.preventDefault();
          event.currentTarget.focus({ preventScroll: true });
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
        onKeyDown={onKeyDown}
        onDoubleClick={() => change((current, fitted) => zoomAt(current, fitted, BUTTON_STEP))}
        style={{
          position: 'relative',
          height,
          borderRadius: 8,
          overflow: 'hidden',
          background: 'color-mix(in srgb, var(--color-bg) 60%, transparent)',
          cursor: moved ? (dragging ? 'grabbing' : 'grab') : 'default',
          touchAction: moved ? 'none' : 'pan-y',
          userSelect: 'none',
        }}
      >
        {view ? (
          <svg
            viewBox={viewBoxString(view)}
            role="img"
            aria-label={title}
            preserveAspectRatio="xMidYMid meet"
            style={{ width: '100%', height: '100%', display: 'block' }}
          >
            {layers.road && paths.road ? <path d={paths.road} fill={LAYER_COLOUR.road} /> : null}

            {layers.centerline && paths.centerline ? (
              <path
                d={paths.centerline}
                fill="none"
                stroke={LAYER_COLOUR.centerline}
                strokeWidth={1}
                strokeDasharray="4 4"
                vectorEffect="non-scaling-stroke"
                opacity={0.8}
              />
            ) : null}

            {layers.borders && paths.borders ? (
              <path
                d={paths.borders}
                fill="none"
                stroke={LAYER_COLOUR.borders}
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            ) : null}

            {layers.gaps && paths.gaps ? (
              <path
                d={paths.gaps}
                fill="none"
                stroke={LAYER_COLOUR.gaps}
                strokeWidth={2.5}
                strokeDasharray="2 4"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            ) : null}

            {layers.walls && paths.walls ? (
              <path
                d={paths.walls}
                fill="none"
                stroke={LAYER_COLOUR.walls}
                strokeWidth={2.5}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            ) : null}

            {layers.finish && paths.finish && geometry.finish ? (
              <>
                <path
                  d={paths.finish}
                  fill="none"
                  stroke={LAYER_COLOUR.finish}
                  strokeWidth={3}
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={(geometry.finish[0] + geometry.finish[2]) / 2}
                  y={-Math.min(geometry.finish[1], geometry.finish[3]) + labelSize * 1.5}
                  fill="var(--color-neutral-400)"
                  fontSize={labelSize}
                  textAnchor="middle"
                  fontFamily="var(--font-body)"
                >
                  S/F
                </text>
              </>
            ) : null}

            {layers.sections && view && centre
              ? (geometry.sections ?? []).map((section) => {
                  if (!section.name) return null;
                  const start = groundToPoint(section.start);
                  const end = groundToPoint(section.end);
                  const middle: [number, number] = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
                  const [x, y] = labelPoint(middle, centre, labelSize * 1.6);
                  return (
                    <text
                      key={`section-${section.n}`}
                      x={x}
                      y={y}
                      fill={LAYER_COLOUR.sections}
                      fontSize={labelSize * 0.85}
                      fontStyle="italic"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontFamily="var(--font-body)"
                      style={{ pointerEvents: 'none' }}
                    >
                      {section.name}
                    </text>
                  );
                })
              : null}

            {layers.corners && view && centre
              ? (geometry.corners ?? []).map((corner) => {
                  const apex = groundToPoint(corner.apex);
                  const radius = labelSize * 0.62;
                  const [bx, by] = labelPoint(apex, centre, radius * 2.6);
                  const picked = corner.n === selectedCorner;
                  // The name sits beyond the badge, on the side away from the
                  // circuit, so it never runs back across the road.
                  const outward = bx >= apex[0];
                  return (
                    <g key={`corner-${corner.n}`} style={{ pointerEvents: 'none' }}>
                      <title>{cornerLabel(corner)}</title>
                      <line
                        x1={apex[0]}
                        y1={apex[1]}
                        x2={bx}
                        y2={by}
                        stroke={LAYER_COLOUR.corners}
                        strokeWidth={1}
                        vectorEffect="non-scaling-stroke"
                        opacity={0.7}
                      />
                      <circle cx={apex[0]} cy={apex[1]} r={radius * 0.28} fill={LAYER_COLOUR.corners} />
                      <circle
                        cx={bx}
                        cy={by}
                        r={radius}
                        fill={picked ? LAYER_COLOUR.corners : 'var(--color-surface)'}
                        stroke={LAYER_COLOUR.corners}
                        strokeWidth={1}
                        vectorEffect="non-scaling-stroke"
                      />
                      <text
                        x={bx}
                        y={by}
                        fill={picked ? 'var(--color-bg)' : 'var(--color-text)'}
                        fontSize={radius * 1.05}
                        fontWeight={600}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontFamily="var(--font-body)"
                        className="tabular-nums"
                      >
                        {corner.n}
                      </text>
                      {corner.name && (named || picked) ? (
                        <text
                          x={bx + (outward ? radius * 1.5 : -radius * 1.5)}
                          y={by}
                          fill="var(--color-text)"
                          fontSize={labelSize * 0.8}
                          textAnchor={outward ? 'start' : 'end'}
                          dominantBaseline="central"
                          fontFamily="var(--font-body)"
                          stroke="var(--color-bg)"
                          strokeWidth={labelSize * 0.18}
                          paintOrder="stroke"
                        >
                          {corner.name}
                        </text>
                      ) : null}
                    </g>
                  );
                })
              : null}

            {children}
          </svg>
        ) : null}

        {bar ? (
          <div
            aria-hidden="true"
            className="tabular-nums"
            style={{
              position: 'absolute',
              left: 12,
              bottom: 10,
              fontSize: 11,
              color: 'var(--color-neutral-400)',
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                width: bar.pixels,
                height: 5,
                borderStyle: 'solid',
                borderColor: 'var(--color-neutral-500)',
                borderWidth: '0 1px 1px 1px',
                marginBottom: 3,
              }}
            />
            {scaleLabel(bar.metres)}
          </div>
        ) : null}

        <div
          style={{ position: 'absolute', right: 10, top: 10, display: 'flex', gap: 4 }}
          // A press on a button is not the start of a drag.
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="btn btn-secondary"
            style={{ padding: '2px 9px' }}
            aria-label="Zoom in"
            onClick={() => change((current, fitted) => zoomAt(current, fitted, BUTTON_STEP))}
          >
            +
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ padding: '2px 9px' }}
            aria-label="Zoom out"
            disabled={zoom <= 1}
            onClick={() => change((current, fitted) => zoomAt(current, fitted, 1 / BUTTON_STEP))}
          >
            −
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ padding: '2px 9px', fontSize: 12 }}
            disabled={zoom <= 1}
            onClick={() => setMoved(null)}
          >
            Fit
          </button>
        </div>
      </div>

      <div role="group" aria-label="Map layers" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {MAP_LAYERS.map((layer) => {
          const empty = counts[layer.id] === 0;
          const shown = layers[layer.id] && !empty;
          return (
            <button
              key={layer.id}
              type="button"
              role="switch"
              aria-checked={shown}
              disabled={empty}
              title={empty ? `${layer.label}: none in this survey` : layer.hint}
              onClick={() => setLayers((current) => ({ ...current, [layer.id]: !current[layer.id] }))}
              className="btn btn-ghost tabular-nums"
              style={{
                fontSize: 12,
                padding: '3px 9px',
                gap: 7,
                opacity: empty ? 0.4 : shown ? 1 : 0.55,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  flex: 'none',
                  background: shown ? LAYER_COLOUR[layer.id] : 'transparent',
                  boxShadow: `inset 0 0 0 1px ${LAYER_COLOUR[layer.id]}`,
                }}
              />
              {layer.label}
              {layer.id === 'gaps' || layer.id === 'walls' || layer.id === 'corners' ? (
                <span style={{ color: 'var(--color-neutral-500)' }}>{counts[layer.id]}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default TrackMap;
